// Built-in live video: peer-to-peer WebRTC with our own WebSocket for signalling, chat and reactions.
// A connection exists between two people when at least one of them is on stage (camera on).
import { BACKEND_URL } from "./api";

const VIEWER_BITRATE = 700_000;

export class RtcRoom {
  constructor(join, onChange) {
    this.join = join;
    this.onChange = onChange;
    this.me = { id: null, stage: join.stage, host: join.host };
    this.peers = new Map();
    this.local = null;
    this.viewers = 0;
    this.status = "connecting";
    this.listeners = { chat: [], react: [], gift: [], caption: [], scene: [], state: [] };
    this.sceneNow = null;
    this.lobby = [];
    this.micOn = true;
    this.camOn = true;
  }

  on(type, fn) { this.listeners[type].push(fn); return () => { this.listeners[type] = this.listeners[type].filter((f) => f !== fn); }; }
  emit() { const st = { me: { ...this.me }, peers: [...this.peers.values()].map(({ pc, cands, ...p }) => p), local: this.local, viewers: this.viewers, status: this.status, micOn: this.micOn, camOn: this.camOn, lobby: this.lobby, sharing: !!this.sharing }; this.onChange?.(st); this.listeners.state.forEach((f) => f(st)); }

  async start() {
    const gen = this.gen = (this.gen || 0) + 1;
    if (this.me.stage) await this.openCamera();
    if (gen !== this.gen) return; // stopped (or restarted, e.g. React StrictMode) while the camera was opening
    const ws = new WebSocket(`${BACKEND_URL.replace(/^http/, "ws")}/api/ws/rtc/${this.join.room}?t=${encodeURIComponent(this.join.ticket)}`);
    this.ws = ws;
    ws.onmessage = (e) => this.handle(JSON.parse(e.data));
    ws.onclose = () => { if (this.ws !== ws) return; if (this.status === "live" || this.status === "connecting" || this.status === "waiting") { this.status = this.status === "connecting" ? "failed" : "disconnected"; this.emit(); } };
    this.ping = setInterval(() => this.send({ type: "ping" }), 20000);
  }

  async openCamera() {
    if (this.local) return;
    const d = this.join.devices || {};
    const want = (dv) => ({ video: { width: { ideal: 1280 }, height: { ideal: 720 }, ...(dv.video ? { deviceId: { exact: dv.video } } : { facingMode: "user" }) }, audio: { echoCancellation: true, noiseSuppression: true, ...(dv.audio ? { deviceId: { exact: dv.audio } } : {}) } });
    const s = await navigator.mediaDevices.getUserMedia(want(d)).catch((e) => { if (d.video || d.audio) return navigator.mediaDevices.getUserMedia(want({})); throw e; });
    if (this.local) { s.getTracks().forEach((t) => t.stop()); return; }
    this.local = s;
    this.micOn = this.camOn = true;
  }

  closeCamera() { this.local?.getTracks().forEach((t) => t.stop()); this.camTrack?.stop(); this.camTrack = null; this.sharing = false; this.local = null; }

  send(m) { if (this.ws?.readyState === 1) this.ws.send(JSON.stringify(m)); }
  chat(text) { this.send({ type: "chat", text }); }
  react(emoji) { this.send({ type: "react", emoji }); }
  hand(up) { this.send({ type: "hand", up }); }
  host(type, peer) { this.send({ type, peer }); }
  end() { this.send({ type: "end" }); }
  scene(s) { this.send({ type: "scene", scene: s }); }
  admit(id) { this.send({ type: "admit", peer: id }); }
  deny(id) { this.send({ type: "deny", peer: id }); }

  toggleMic() { this.micOn = !this.micOn; this.local?.getAudioTracks().forEach((t) => { t.enabled = this.micOn; }); this.emit(); }
  toggleCam() { this.camOn = !this.camOn; this.local?.getVideoTracks().forEach((t) => { t.enabled = this.camOn; }); this.emit(); }

  // Swap the camera or mic without leaving the room.
  async switchDevice(kind, deviceId) {
    if (!this.local) return;
    const s = await navigator.mediaDevices.getUserMedia(kind === "video" ? { video: { deviceId: { exact: deviceId }, width: { ideal: 1280 }, height: { ideal: 720 } } } : { audio: { deviceId: { exact: deviceId }, echoCancellation: true, noiseSuppression: true } });
    const t = s.getTracks()[0];
    t.enabled = kind === "video" ? this.camOn : this.micOn;
    this.local.getTracks().filter((x) => x.kind === t.kind).forEach((x) => { this.local.removeTrack(x); x.stop(); });
    this.local.addTrack(t);
    this.join.devices = { ...(this.join.devices || {}), [kind]: deviceId };
    if (kind === "video" || !this.audioOut) this.peers.forEach((p) => p.pc?.getSenders().forEach((sn) => sn.track?.kind === t.kind && sn.replaceTrack(t)));
    this.local = new MediaStream(this.local.getTracks());
    this.emit();
  }

  // Replace what others hear from me (e.g. my mic mixed with phone callers). null = back to my mic.
  setAudioOut(track) {
    this.audioOut = track;
    const t = track || this.local?.getAudioTracks()[0];
    if (t) this.peers.forEach((p) => p.pc?.getSenders().forEach((sn) => sn.track?.kind === "audio" && sn.replaceTrack(t)));
  }

  // Share my screen in place of my camera; ending the share (or the browser's Stop button) brings the camera back.
  async shareScreen() {
    if (this.sharing || !this.local) return;
    const s = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: 15 }, audio: false });
    const t = s.getVideoTracks()[0];
    this.camTrack = this.local.getVideoTracks()[0];
    this.peers.forEach((p) => p.pc?.getSenders().forEach((sn) => sn.track?.kind === "video" && sn.replaceTrack(t)));
    this.local = new MediaStream([t, ...this.local.getAudioTracks()]);
    this.sharing = true;
    t.onended = () => this.stopShare();
    this.emit();
  }

  stopShare() {
    if (!this.sharing) return;
    const cam = this.camTrack;
    this.local?.getVideoTracks().forEach((x) => x.stop());
    if (cam) this.peers.forEach((p) => p.pc?.getSenders().forEach((sn) => sn.track?.kind === "video" && sn.replaceTrack(cam)));
    this.local = new MediaStream([...(cam ? [cam] : []), ...(this.local?.getAudioTracks() || [])]);
    this.sharing = false;
    this.emit();
  }

  needs(p) { return this.me.stage || p.stage; }

  newPc(p) {
    const pc = new RTCPeerConnection({ iceServers: this.join.ice });
    p.pc = pc; p.cands = [];
    pc.onicecandidate = (e) => e.candidate && this.send({ type: "signal", to: p.id, data: { candidate: e.candidate } });
    pc.ontrack = (e) => { p.stream = e.streams[0] || p.stream || new MediaStream(); if (!e.streams[0]) p.stream.addTrack(e.track); this.emit(); };
    pc.onconnectionstatechange = () => { p.conn = pc.connectionState; this.emit(); };
    if (this.me.stage && this.local) this.local.getTracks().forEach((t) => pc.addTrack(t.kind === "audio" && this.audioOut ? this.audioOut : t, this.local));
    else { pc.addTransceiver("video", { direction: "recvonly" }); pc.addTransceiver("audio", { direction: "recvonly" }); }
    return pc;
  }

  async cap(p) {
    if (!this.me.stage || p.stage) return;
    for (const s of p.pc.getSenders()) {
      if (s.track?.kind !== "video") continue;
      const prm = s.getParameters();
      prm.encodings = prm.encodings?.length ? prm.encodings : [{}];
      prm.encodings[0].maxBitrate = VIEWER_BITRATE;
      try { await s.setParameters(prm); } catch { /* browser may not allow */ }
    }
  }

  drop(p) { try { p.pc?.close(); } catch { /* noop */ } p.pc = null; p.stream = null; }

  async offer(p) {
    this.drop(p);
    const pc = this.newPc(p);
    await pc.setLocalDescription(await pc.createOffer());
    this.send({ type: "signal", to: p.id, data: { sdp: pc.localDescription } });
    this.cap(p);
  }

  async connectAll() { for (const p of this.peers.values()) { if (this.needs(p)) await this.offer(p); else this.drop(p); } this.emit(); }

  async handle(m) {
    if (m.type === "welcome") {
      this.me = { ...this.me, id: m.you, stage: m.stage, host: m.host };
      m.peers.forEach((p) => this.peers.set(p.id, { ...p }));
      if (!m.stage) this.closeCamera();
      this.status = "live";
      this.sceneNow = m.scene || null;
      this.lobby = m.lobby || [];
      if (m.now) this.skew = m.now - Date.now() / 1000;
      this.listeners.scene.forEach((f) => f(this.sceneNow));
      await this.connectAll();
    } else if (m.type === "waiting") { this.status = "waiting"; this.emit(); }
    else if (m.type === "denied") { this.status = "denied"; this.emit(); }
    else if (m.type === "lobby") { this.lobby = m.lobby || []; this.emit(); }
    else if (m.type === "joined") { this.peers.set(m.peer.id, { ...m.peer }); this.emit(); }
    else if (m.type === "left") { const p = this.peers.get(m.peer); if (p) this.drop(p); this.peers.delete(m.peer); this.emit(); }
    else if (m.type === "peer") { const p = this.peers.get(m.peer.id); if (p) Object.assign(p, m.peer); this.emit(); }
    else if (m.type === "role") {
      if (m.peer.id === this.me.id) {
        this.me.stage = m.peer.stage;
        try { if (this.me.stage) await this.openCamera(); else this.closeCamera(); } catch { this.me.stage = false; }
        await this.connectAll();
      } else {
        const p = this.peers.get(m.peer.id);
        if (p) { Object.assign(p, m.peer); if (!this.needs(p)) this.drop(p); }
        this.emit();
      }
    } else if (m.type === "signal") {
      const p = this.peers.get(m.from);
      if (!p) return;
      const d = m.data;
      if (d.sdp?.type === "offer") {
        this.drop(p);
        const pc = this.newPc(p);
        await pc.setRemoteDescription(d.sdp);
        await pc.setLocalDescription(await pc.createAnswer());
        this.send({ type: "signal", to: p.id, data: { sdp: pc.localDescription } });
        for (const c of p.cands) await pc.addIceCandidate(c).catch(() => {});
        p.cands = [];
        this.cap(p);
      } else if (d.sdp?.type === "answer" && p.pc) {
        await p.pc.setRemoteDescription(d.sdp).catch(() => {});
        for (const c of p.cands) await p.pc.addIceCandidate(c).catch(() => {});
        p.cands = [];
      } else if (d.candidate) {
        if (p.pc?.remoteDescription) await p.pc.addIceCandidate(d.candidate).catch(() => {});
        else (p.cands = p.cands || []).push(d.candidate);
      }
    } else if (m.type === "count") { this.viewers = m.viewers; this.emit(); }
    else if (m.type === "scene") { this.sceneNow = m.scene; if (m.now) this.skew = m.now - Date.now() / 1000; this.listeners.scene.forEach((f) => f(m.scene)); }
    else if (["chat", "react", "gift", "caption"].includes(m.type)) this.listeners[m.type].forEach((f) => f(m));
    else if (["ended", "kicked", "full"].includes(m.type)) { this.status = m.type; this.detail = m.detail; this.emit(); this.stop(); }
    else if (m.type === "error") this.listeners.chat.forEach((f) => f({ id: Math.random().toString(36), name: "Live", text: m.detail, system: true }));
  }

  stop() {
    this.gen = (this.gen || 0) + 1;
    clearInterval(this.ping);
    this.peers.forEach((p) => this.drop(p));
    this.peers.clear();
    this.closeCamera();
    const ws = this.ws;
    this.ws = null;
    try { ws?.close(); } catch { /* noop */ }
  }
}
